import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/glucose";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Plus, Trash, Eye } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Watchers() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ watched_email: "", label: "" });

  const load = useCallback(async () => {
    try {
      const r = await api.get("/watchers");
      setRows(r.data || []);
    } catch {
      toast.error("Failed to load followers");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.watched_email || !form.label) {
      toast.error("Email and label required");
      return;
    }
    try {
      await api.post("/watchers", form);
      toast.success("Follower added");
      setForm({ watched_email: "", label: "" });
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const remove = async (id) => {
    try { await api.delete(`/watchers/${id}`); load(); } catch (e) { console.error(e); toast.error("Failed to remove"); }
  };

  return (
    <div className="space-y-6" data-testid="watchers-root">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">Caregiver view</div>
          <h1 className="font-display text-2xl sm:text-3xl tracking-tight">Followers</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">
            Add another GlucoCloud user by email to watch their live glucose data.
            They must have signed in at least once. Pending means they haven&apos;t signed up yet.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-watcher-btn" className="gap-1"><Plus size={16} /> Add follower</Button>
          </DialogTrigger>
          <DialogContent data-testid="watcher-dialog">
            <DialogHeader><DialogTitle>Add follower</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label>Their email</Label>
                <Input type="email" value={form.watched_email} onChange={(e) => setForm({ ...form, watched_email: e.target.value })} data-testid="watcher-email-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Label (nickname)</Label>
                <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="watcher-label-input" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={add} data-testid="submit-watcher">Add</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.length === 0 && <div className="text-sm text-muted-foreground" data-testid="watchers-empty">You aren&apos;t following anyone yet.</div>}
        {rows.map((w) => (
          <div key={w.id} className="p-5 rounded-md border border-border bg-card" data-testid={`watcher-item-${w.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{w.watched_user_id ? "Active" : "Pending sign-up"}</div>
                <div className="font-display text-lg mt-1">{w.label}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{w.watched_email}</div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => remove(w.id)} data-testid={`remove-watcher-${w.id}`}><Trash size={14} /></Button>
            </div>
            {w.watched_user_id && (
              <Link to={`/dashboard?patient=${w.watched_user_id}`} className="mt-4 inline-flex items-center gap-1 text-xs underline text-foreground" data-testid={`view-watcher-${w.id}`}>
                <Eye size={12} /> View live data
              </Link>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
